import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Upload, Crop as CropIcon, X, RefreshCw, ImageIcon } from "lucide-react";

/**
 * Returns a cropped image blob (image/jpeg) by drawing the source onto a canvas.
 * High-quality output: smoothing enabled, 0.92 quality.
 */
async function getCroppedBlob(imageSrc, pixelCrop, outputType = "image/jpeg", quality = 0.92) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height,
  );
  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), outputType, quality));
}

const ASPECT_PRESETS = [
  { key: "image",  label: "Original",  ratio: null },  // image's natural aspect (no crop change)
  { key: "1:1",    label: "1:1",       ratio: 1 },
  { key: "4:3",    label: "4:3",       ratio: 4 / 3 },
  { key: "3:4",    label: "3:4",       ratio: 3 / 4 },
  { key: "16:9",   label: "16:9",      ratio: 16 / 9 },
  { key: "16:5",   label: "16:5",      ratio: 16 / 5 },
];

/**
 * ImageCropper: file picker → flexible crop modal → upload to /files/upload.
 *
 * Props:
 *   label, fileId, onUpload(id), onClear(), testId
 *   aspect: optional default aspect ratio. Omit/undefined => default to "Original" preset (image's natural aspect).
 *   aspectLabel: small text shown next to the field label (defaults to "Free size").
 */
export default function ImageCropper({ label, fileId, onUpload, onClear, testId, aspect, aspectLabel }) {
  const defaultPresetKey = aspect ? "custom" : "image";
  const labelText = aspectLabel || (aspect ? "Aspect locked" : "Free size");

  const inputRef = React.useRef(null);
  const [src, setSrc] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [naturalAspect, setNaturalAspect] = useState(null);
  const [presetKey, setPresetKey] = useState(defaultPresetKey);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Resolve the effective aspect ratio passed to react-easy-crop.
  // If the user picked a preset, use it. "image" / "free" => use the image's natural aspect.
  let effectiveAspect = naturalAspect || 1;
  if (presetKey === "custom" && aspect) effectiveAspect = aspect;
  else {
    const preset = ASPECT_PRESETS.find((p) => p.key === presetKey);
    if (preset?.ratio) effectiveAspect = preset.ratio;
  }

  const previewHeight = effectiveAspect ? Math.round(96 / effectiveAspect) : 96;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOriginalFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setPixels(null);
      const img = new Image();
      img.onload = () => setNaturalAspect(img.naturalWidth / img.naturalHeight);
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onCropComplete = useCallback((_area, areaPixels) => { setPixels(areaPixels); }, []);

  // Live crop preview — debounce-free since react-easy-crop only fires on interaction end.
  useEffect(() => {
    if (!src || !pixels) { setPreviewUrl(null); return; }
    let cancelled = false;
    let createdUrl = null;
    getCroppedBlob(src, pixels).then((blob) => {
      if (cancelled || !blob) return;
      createdUrl = URL.createObjectURL(blob);
      setPreviewUrl(createdUrl);
    });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [src, pixels]);

  const closeModal = () => {
    setSrc(null);
    setOriginalFile(null);
    setPreviewUrl(null);
    setPresetKey(defaultPresetKey);
  };

  const uploadBlob = async (blob, fileName) => {
    const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
    const file = new File([blob], (fileName || "upload").replace(/\.[^.]+$/, "") + "." + ext, { type: blob.type });
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.id;
  };

  const saveCropped = async () => {
    if (!src || !pixels) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(src, pixels);
      const id = await uploadBlob(blob, originalFile?.name);
      onUpload(id);
      toast.success("Image cropped & uploaded");
      closeModal();
    } catch {
      toast.error("Crop or upload failed");
    } finally { setUploading(false); }
  };

  const saveOriginal = async () => {
    if (!originalFile) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", originalFile);
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpload(r.data.id);
      toast.success("Original image uploaded");
      closeModal();
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div data-testid={`${testId}-wrap`}>
      <label className="block text-sm font-medium mb-2">
        {label}
        <span className="ml-2 text-[10px] font-normal text-[#525252] uppercase tracking-wider">{labelText}</span>
      </label>

      <div className="border border-dashed border-black/20 rounded-sm p-3 bg-[#FAFAFA]">
        {fileId ? (
          <div className="flex items-center gap-3">
            <div
              data-testid={`${testId}-preview`}
              className="rounded-sm bg-white overflow-hidden border border-black/10"
              style={{ width: 96, height: previewHeight }}
            >
              <img src={`/api/files/${fileId}`} alt="" loading="lazy"
                   className="w-full h-full object-cover" />
            </div>
            <div className="flex flex-col gap-1">
              <button type="button" onClick={() => inputRef.current?.click()}
                      data-testid={`${testId}-replace`}
                      className="text-sm text-[#0A0A0A] inline-flex items-center gap-1 hover:text-[var(--brand-primary)]">
                <RefreshCw size={12}/> Replace
              </button>
              <button type="button" onClick={onClear} data-testid={`${testId}-clear`}
                      className="text-sm text-[var(--brand-primary)] inline-flex items-center gap-1">
                <X size={12}/> Remove
              </button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()}
                  data-testid={`${testId}-upload`}
                  className="inline-flex items-center gap-2 text-sm text-[#525252] hover:text-[#0A0A0A]">
            <Upload size={14}/> Choose image
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile}/>
      </div>

      {src && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid={`${testId}-modal`}>
          <div className="bg-white rounded-sm max-w-4xl w-full max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-black/10">
              <h3 className="font-display text-lg font-bold inline-flex items-center gap-2">
                <CropIcon size={16}/> Adjust image
              </h3>
              <button type="button" onClick={closeModal} className="p-1 hover:bg-black/5"
                      data-testid={`${testId}-cancel`}>
                <X size={18}/>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr,260px] flex-1 min-h-0">
              {/* Crop canvas */}
              <div className="relative min-h-[360px] lg:min-h-0 bg-[#0A0A0A]">
                <Cropper
                  image={src}
                  crop={crop}
                  zoom={zoom}
                  aspect={effectiveAspect}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  objectFit="contain"
                  restrictPosition={false}
                />
              </div>

              {/* Side panel: aspect presets + preview */}
              <aside className="p-4 border-t lg:border-t-0 lg:border-l border-black/10 overflow-y-auto">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#525252] mb-2">Aspect ratio</div>
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {ASPECT_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setPresetKey(p.key)}
                      data-testid={`${testId}-aspect-${p.key}`}
                      className={`px-2.5 py-1 text-xs rounded-sm border transition-colors ${
                        presetKey === p.key
                          ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                          : "bg-white text-[#0A0A0A] border-black/10 hover:border-black/30"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                <div className="text-[10px] uppercase tracking-[0.18em] text-[#525252] mb-2">Zoom</div>
                <input type="range" min="1" max="3" step="0.05"
                       value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
                       className="w-full mb-5" data-testid={`${testId}-zoom`}/>

                <div className="text-[10px] uppercase tracking-[0.18em] text-[#525252] mb-2">Preview</div>
                <div className="border border-black/10 rounded-sm bg-[#FAFAFA] aspect-square w-full overflow-hidden flex items-center justify-center mb-4"
                     data-testid={`${testId}-preview-pane`}>
                  {previewUrl ? (
                    <img src={previewUrl} alt="crop preview" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <div className="text-[11px] text-[#525252] inline-flex items-center gap-1.5">
                      <ImageIcon size={12}/> Adjust to preview
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-[#525252] leading-relaxed">
                  Drag inside the canvas to reposition. Use the slider to zoom. Switch aspect anytime, or skip cropping below.
                </p>
              </aside>
            </div>

            <div className="p-4 border-t border-black/10 flex flex-wrap gap-3">
              <button type="button" onClick={saveOriginal} disabled={uploading || !originalFile}
                      data-testid={`${testId}-save-original`}
                      className="px-4 py-2 border border-black/15 rounded-sm hover:bg-black/5 inline-flex items-center gap-2 text-sm font-medium disabled:opacity-60">
                <ImageIcon size={14}/> Use original (no crop)
              </button>
              <div className="flex-1"/>
              <button type="button" onClick={closeModal}
                      className="px-4 py-2 border border-black/10 rounded-sm hover:bg-black/5 text-sm">
                Cancel
              </button>
              <button type="button" onClick={saveCropped} disabled={uploading || !pixels}
                      data-testid={`${testId}-save`}
                      className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-5 py-2 rounded-sm font-medium disabled:opacity-60 text-sm">
                {uploading ? "Uploading..." : "Crop & Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
